import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendJSONRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import type { CreateSessionModeDto } from '@/types/dtos/sessions/request/create-session-mode.dto.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { GET_EVENT_CONTRIBUTORS_QUERY_KEY } from '@/state/queries/contributors/use-get-event-contributors';
import { invalidatePublicationStateQueries } from '@/state/queries/events/use-publication-state';

export const useCreateSessionModeMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: CreateSessionModeDto) => {
      const token = await getToken();
      return createSessionMode(params, token ?? '');
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_SESSIONS_QUERY_KEY],
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY],
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_FLOW_QUERY_KEY],
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_SESSION_QUERY_KEY],
          refetchType: 'all',
        }),
        queryClient.invalidateQueries({
          queryKey: [GET_EVENT_CONTRIBUTORS_QUERY_KEY],
        }),
        invalidatePublicationStateQueries(queryClient),
      ]);
    },
  });
};

const createSessionMode = async (params: CreateSessionModeDto, token: string): Promise<SessionDto> => {
  const { id, mode } = params;
  const url = `${import.meta.env.VITE_API_URL}/sessions/${id}/mode`;
  return sendJSONRequest({ url, method: METHOD.POST, body: { mode }, token });
};

export default useCreateSessionModeMutation;
